/**
 * @file RepStandingOrderView.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-30
 * @description A standing order, read only, on a rep's phone. Cam, 28 Oct
 *              2026 (rep mobile review, "All 20"): "Tapping a standing order
 *              card only highlights it. Nothing opens." — suggested "open the
 *              standing order, read only, on a tap".
 *
 * Why not StandingOrderModal: that is the office's edit form — every field
 * an input, Save at the foot. A rep cannot save (the read-only guard refuses
 * it), so on a phone it would be a form that looks editable and is not. This
 * is the same standing order said in words: who, what, how often, when it
 * was last had and is next due, the notes, and whether it is held. Built on
 * the shared Modal and the search row the card already has — no fetch.
 *
 * 1.1.0 (2026-10-30, Cam — rep order requests): "Request a change" beside
 * Customer when `onRequestChange` is given — a request to the office, filled
 * in from this standing order (RepChangeRequestModal).
 */

import React from 'react';
import Modal from './Modal';
import { repStandingOrderCard } from '../utils/repDocCards';
import './RepOrderCard.css';
import './RepStandingOrderView.css';

function Row({ label, children }) {
  if (children == null || children === '' || children === false) return null;
  return (
    <div className="rsov-row">
      <span className="rsov-label">{label}</span>
      <span className="rsov-value">{children}</span>
    </div>
  );
}

function RepStandingOrderView({ standingOrder, onClose, onOpenCustomer, onRequestChange = null }) {
  if (!standingOrder) return null;
  const so = standingOrder;
  const card = repStandingOrderCard(so);
  const products = Array.isArray(so.products) ? so.products : [];

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Standing order"
      size="md"
      className="rep-standing-order-view"
    >
      <div className="rsov-head">
        <div className="rsov-who">
          <span className="roc-customer">{card.title}</span>
          {card.place && <span className="roc-place">{card.place}</span>}
        </div>
        <span className={`roc-chip tone-${card.chip.tone}`}>{card.chip.label}</span>
      </div>

      {card.flags.length > 0 && (
        <div className="roc-flags">
          {card.flags.map(f => <span key={f.key} className="roc-flag">{f.label}</span>)}
        </div>
      )}

      <div className="rsov-rows">
        <Row label="Schedule">{card.schedule}</Row>
        <Row label="Last had">{card.lastText}</Row>
        <Row label="Next due">{card.nextText}</Row>
        <Row label="Notes">{card.notes}</Row>
      </div>

      <div className="rsov-products">
        <span className="rsov-label">{products.length > 1 ? 'Products — the first is the main one' : 'Product'}</span>
        {(products.length ? products : [{ item_code: so.product_code, item_name: so.product_name }]).map((p, i) => (
          <div key={`${p.item_id || p.item_code || i}`} className="rsov-product">
            {p.item_code && <span className="roc-code">{p.item_code}</span>}
            <span className="rsov-product-name">{p.item_name || p.item_code || 'No product'}</span>
            {p.is_archived && <span className="roc-chip tone-error">Archived</span>}
            {Array.isArray(p.additives) && p.additives.length > 0 && (
              <span className="rsov-product-add">
                + {p.additives.map(a => a && (a.name || a.additive_name || a.code)).filter(Boolean).join(' + ')}
              </span>
            )}
          </div>
        ))}
        {so.additive_name && !products.some(p => Array.isArray(p.additives) && p.additives.length) && (
          <div className="rsov-product-add">+ {so.additive_name}</div>
        )}
      </div>

      <div className="roc-actions rsov-actions">
        <button
          type="button"
          data-read-safe
          className="roc-action"
          onClick={onOpenCustomer || undefined}
          disabled={!onOpenCustomer}
          title={onOpenCustomer ? undefined : 'No customer account on this standing order'}
        >
          <span className="material-symbols-rounded" aria-hidden="true">person</span>
          Customer
        </button>
        {onRequestChange && (
          <button
            type="button"
            data-read-safe
            className="roc-action"
            onClick={onRequestChange}
            title="Ask the office to change this standing order"
          >
            <span className="material-symbols-rounded" aria-hidden="true">edit_note</span>
            Request a change
          </button>
        )}
      </div>
    </Modal>
  );
}

export default RepStandingOrderView;
